import React from "react";
import { getTemplate } from "../lib/templates";
import { formatSlotTime } from "../lib/timeFormat";
import { normInd, rgbaFromHex, matchIndicator, indicatorCellStyle, DEFAULT_SLOT_FOMO_INDICATORS } from "../lib/fomo";

// ── Shared booking-flow header (logo + title + subtitle + welcome) ──
// Rendered identically on every booking flow page so editor changes reflect everywhere.
// Soft halo behind header text so stylised/distressed fonts stay legible over
// busy image/video backgrounds. Strength 0 = off (lets template defaults show).
export function headerTextShadow(b) {
  const s = Number(b?.header_text_shadow || 0);
  if (!s) return null;
  const c = String(b.header_text_shadow_color || "#000000").replace("#", "");
  if (c.length !== 6) return null;
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), bl = parseInt(c.slice(4, 6), 16);
  const a = Math.min(0.9, 0.3 + (s / 100) * 0.6);
  const blur = Math.round((s / 100) * 18) + 2;
  const rgba = `rgba(${r}, ${g}, ${bl}, ${a})`;
  return `0 1px 2px ${rgba}, 0 0 ${blur}px ${rgba}`;
}

export function BookingHeader({ attraction, branding }) {
  const b = branding || {};
  const a = attraction || {};
  const tplId = getTemplate(b.template_id || "default").id;
  const shadow = headerTextShadow(b);
  const strokeW = Number(b.header_text_stroke_width || 0);
  const stroke = strokeW > 0
    ? { WebkitTextStrokeWidth: `${strokeW}px`, WebkitTextStrokeColor: b.header_text_stroke_color || "#000000" }
    : {};
  const titleText = b.title_text || a.name || "";
  const titleStyle = {
    color: b.title_color || "var(--text-color,#fff)",
    ...(b.title_size ? { fontSize: `${b.title_size}px` } : {}),
    ...(b.title_weight ? { fontWeight: b.title_weight } : {}),
    ...(b.title_transform ? { textTransform: b.title_transform } : {}),
    ...(b.title_letter_spacing != null ? { letterSpacing: `${b.title_letter_spacing}px` } : {}),
    ...(b.title_align ? { textAlign: b.title_align } : {}),
    ...(shadow ? { textShadow: shadow } : {}),
    ...stroke,
  };
  const subtitle = b.subtitle_text;
  const subtitleStyle = {
    color: b.subtitle_color || "var(--text-color,#bbb)",
    fontSize: `${b.subtitle_size || 14}px`,
    fontWeight: b.subtitle_weight || 400,
    ...(b.subtitle_align ? { textAlign: b.subtitle_align } : {}),
    ...(shadow ? { textShadow: shadow } : {}),
    ...stroke,
  };
  const welcomeText = b.welcome_text;
  const wAlign = b.welcome_align || (tplId === "carnival" || tplId === "crypt" ? "center" : "left");
  const welcomeStyle = {
    ...(b.welcome_font ? { fontFamily: `'${b.welcome_font}'` } : {}),
    fontSize: `${b.welcome_size || 14}px`,
    fontWeight: b.welcome_weight || 400,
    color: b.welcome_color || "var(--text-color,#888)",
    textAlign: wAlign,
    marginLeft: wAlign === "center" || wAlign === "right" ? "auto" : 0,
    marginRight: wAlign === "center" ? "auto" : 0,
    ...(shadow ? { textShadow: shadow } : {}),
    ...stroke,
  };
  return (
    <div className="booking-header">
      <div className="booking-header-inner" data-testid="booking-header-inner">
        {b.logo_url && (
          <div className="logo-slot" data-testid="header-logo-slot">
            <img src={b.logo_url} alt={titleText} className="attraction-logo" />
          </div>
        )}
        <h1 className="public-display attraction-name text-4xl md:text-5xl font-bold" style={titleStyle} data-testid="event-attraction-name">{titleText}</h1>
        {tplId === "crypt" && <hr className="crypt-rule" data-testid="crypt-rule" />}
        {subtitle && <p className="attraction-tagline" style={subtitleStyle} data-testid="attraction-tagline">{subtitle}</p>}
        {welcomeText && <p className="welcome-text" style={welcomeStyle} data-testid="welcome-text">{welcomeText}</p>}
      </div>
    </div>
  );
}

// ── Shared booking summary panel, used across calendar + time-slot + ticket steps ──
export function SummaryPanel({ heading, dateText, showTime = true, timeText = "—", ticketsText = "—", totalText = "—", cta, testid = "summary-panel" }) {
  return (
    <aside className="pc-panel info-panel" data-testid={testid}>
      <h3 className="info-heading" data-testid="summary-heading">{heading}</h3>
      <div className="info-row"><span className="info-label">Date</span><span className="info-value" data-testid="summary-date">{dateText || "—"}</span></div>
      {showTime && <div className="info-row"><span className="info-label">Time</span><span className="info-value" data-testid="summary-time">{timeText || "—"}</span></div>}
      <div className="info-row"><span className="info-label">Tickets</span><span className="info-value" data-testid="summary-tickets">{ticketsText}</span></div>
      <div className="info-row"><span className="info-label">Total</span><span className="info-value" data-testid="summary-total">{totalText}</span></div>
      {cta && <button className="info-cta" disabled={cta.disabled} onClick={cta.onClick} data-testid="summary-cta">{cta.label}</button>}
    </aside>
  );
}

// ── FOMO colour-key legend ──
export function FomoKey({ indicators, testid = "fomo-key" }) {
  const items = (indicators || []).map(normInd);
  if (items.length === 0) return null;
  return (
    <div className="pc-key" data-testid={testid}>
      {items.map((i) => (
        <span key={i.id} className="pc-key-item" style={{ color: i.label_color }} data-testid={`${testid}-${i.id}`}>
          <span className="pc-key-dot" style={{ background: rgbaFromHex(i.cell_bg_color, i.cell_bg_opacity), borderColor: i.cell_border_color }} />{i.label}
        </span>
      ))}
    </div>
  );
}

// Returns a hint string if a slot CANNOT fulfil the requested ticket quantities, else null.
// Per-type caps (ticket_type.max_per_slot) take priority; then the slot's total remaining.
export function slotShortfall(slot, items, ticketTypes) {
  const totalReq = Object.values(items || {}).reduce((sum, q) => sum + Number(q || 0), 0);
  if (totalReq <= 0 || !slot || slot.sold_out) return null;
  for (const tt of (ticketTypes || [])) {
    const q = Number((items || {})[tt.id] || 0);
    if (q <= 0) continue;
    const ov = (slot.type_overrides && slot.type_overrides[tt.id]) || {};
    const cap = ov.capacity_override != null ? ov.capacity_override : tt.max_per_slot;
    if (cap == null) continue; // unlimited for this type
    const sold = (slot.type_sold && slot.type_sold[tt.id]) || 0;
    const typeRem = Math.max(0, Number(cap) - sold);
    if (q > typeRem) return `Only ${typeRem} ${tt.name} left`;
  }
  const cap = slot.effective_capacity ?? slot.max_capacity ?? 0;
  const totalRemaining = Math.max(0, cap - (slot.tickets_sold || 0) - (slot.tickets_held || 0));
  if (totalReq > totalRemaining) return `Only ${totalRemaining} left — reduce tickets`;
  return null;
}

// ── Time slot selection box (.timeslot-container) with slot styling + slot-level FOMO ──
export function TimeslotPicker({ slots, branding, attraction, selectedId, onSelect, items = {}, ticketTypes = [] }) {
  const b = branding || {};
  const heading = b.timeslot_container_heading || "Choose a time";
  const fomoOn = !!b.slot_fomo_enabled;
  const indicators = (b.slot_fomo_indicators && b.slot_fomo_indicators.length) ? b.slot_fomo_indicators : DEFAULT_SLOT_FOMO_INDICATORS;
  const showAvail = !!b.slot_show_availability;
  const list = slots || [];

  return (
    <div className="timeslot-container" data-testid="timeslot-container">
      <h2 className="pc-heading" data-testid="timeslot-heading">{heading}</h2>
      {list.length === 0 ? (
        <div className="text-center py-8 text-[#8a8a8a] text-sm" data-testid="no-slots">No time slots for this date.</div>
      ) : (
        <div className="slot-grid" data-testid="slot-grid">
          {list.map((s) => {
            const cap = s.effective_capacity ?? s.max_capacity ?? 1;
            // Urgency badge & "remaining" key off the per-ticket-type cap when present
            // (fomo_capacity/fomo_sold from the API) so e.g. a type capped at 30 reads X/30
            // even when the slot's overall max_capacity is higher.
            const fomoCap = s.fomo_capacity != null ? s.fomo_capacity : cap;
            const fomoSold = s.fomo_sold != null ? s.fomo_sold : s.tickets_sold;
            const remaining = fomoCap != null ? Math.max(0, fomoCap - fomoSold) : Math.max(0, cap - s.tickets_sold);
            // Reflect the most sold-out ticket type; never hit the 100%/"sold out" tier while still bookable.
            const fill = s.sold_out ? 100 : (fomoCap ? Math.min(99, Math.round(100 * fomoSold / Math.max(1, fomoCap))) : 0);
            const ind = fomoOn ? matchIndicator(indicators, fill) : null;
            const sel = selectedId === s.id;
            const shortfall = slotShortfall(s, items, ticketTypes);
            const disabled = s.sold_out || !!shortfall;
            const style = ind && !sel && !shortfall ? indicatorCellStyle(ind) : undefined;
            return (
              <button key={s.id} disabled={disabled} onClick={() => onSelect && onSelect(s)} style={style}
                      data-testid={`slot-${s.start_time}`} className={`slot-item ${sel ? "slot-selected" : ""} ${shortfall ? "slot-blocked" : ""}`}>
                <span className="slot-time">{formatSlotTime(s.start_time, s.end_time, attraction)}</span>
                {s.sold_out
                  ? <span className="slot-remaining">Sold out</span>
                  : shortfall
                    ? <span className="slot-remaining slot-shortfall" data-testid={`slot-shortfall-${s.start_time}`}>{shortfall}</span>
                    : fomoOn && ind
                      ? <span className="slot-remaining" style={{ color: normInd(ind).label_color }} data-testid={`slot-indicator-${s.start_time}`}>{ind.label}</span>
                      : showAvail
                        ? <span className="slot-remaining">{remaining} remaining</span>
                        : null}
              </button>
            );
          })}
        </div>
      )}
      {fomoOn && list.length > 0 && <FomoKey indicators={indicators} testid="slot-fomo-key" />}
    </div>
  );
}
